import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData, transformData } from '../../../util/helper';
import { getEventMasterlistIntroductionData, updateEventMasterlistData } from '../../../services/EventMasterlist';
import { fetchPersonalityAliasSuggestions as fetchPersonalityAliasSuggestionsCommon } from '../../../services/Personality';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { RjsfSubmitButton } from '../../../RJSFCommonSchemas';
import FormLoader from '../../../CommonComponents/FormLoader';
import { showErrorToast, showInfoToast, showSuccessToast } from "../../../util/errorHandler";


const Highlights = () => {

  const { alias } = useParams();
  const [processedData, setProcessedData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();


  const schema = {
    "title": "",
    "description": "",
    "type": "object",
    "properties": {
      "artists": {
        "type": "array",
        "title": "Artists",
        "items": {
          "type": "object",
          "properties": {
            "alias": {
              "type": "string",
              "title": "Alias"
            },
            "name": {
              "type": "string",
              "title": "Name"
            },
            "full_name": {
              "type": "string",
              "title": "Full Name"
            }
          }
        }
      }
    }
  }

  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    "name": {
      "ui:readonly": true,
      "ui:disabled": true,
    },
    "full_name": {
      "ui:readonly": true,
      "ui:disabled": true,
    },
    artists: {
      "ui:emptyValue": "",
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead",
        },
        name: {
          "ui:readonly": true,
          "ui:disabled": true,
        },
        full_name: {
          "ui:readonly": true,
          "ui:disabled": true,
        }
      },
    }
  };

  // Fetch initial data
  useEffect(() => {
    const getHighlightsData = async () => {
      try {
        if (alias) {
          const result = await getEventMasterlistIntroductionData(alias, 'highlights');
          if (result) {
            const fieldsToTransform = [
            ];
            const transformedData = transformData(result?.[0], fieldsToTransform);

            const firstArtist = transformedData?.artists?.[0];
            if (firstArtist) {
              transformedData.full_name = firstArtist.full_name || '';
              transformedData.name = firstArtist.name || '';
            }

            setProcessedData(transformedData);
            setOriginalData(transformedData); // Store original data for comparison
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getHighlightsData();
  }, [alias, quickSave, toast]);

  const fetchPersonalityAliasSuggestions = (inputValue) =>
    fetchPersonalityAliasSuggestionsCommon(inputValue, {
      onError: () =>
                showErrorToast(toast, "Failed to load personality suggestions")
    });

  // Function to identify changed items (added, updated, deleted)
  const getChangedItems = (currentData, originalData, fieldName) => {
    const currentArray = currentData[fieldName] || [];
    const originalArray = originalData[fieldName] || [];

    const changedItems = [];
    const processedOriginalAliases = new Set();

    // Create a map of original items by alias for efficient lookup
    const originalMap = new Map();
    originalArray.forEach(item => {
      if (item.alias) {
        originalMap.set(item.alias, item);
      }
    });

    // Check current items against original
    currentArray.forEach(currentItem => {
      if (currentItem.alias && originalMap.has(currentItem.alias)) {
        // Item exists in original - check if it's been updated
        const originalItem = originalMap.get(currentItem.alias);

        // Compare each field individually
        const hasChanges = Object.keys(currentItem).some(key => {
          return currentItem[key] !== originalItem[key];
        });

        if (hasChanges) {
          // This is an updated item
          changedItems.push({
            ...currentItem,
            type: 'update'
          });
        }

        processedOriginalAliases.add(currentItem.alias);
      } else {
        // No alias or not in original - this is a new item
        changedItems.push({
          ...currentItem,
          type: 'add'
        });
      }
    });

    // Check for deleted items (items that exist in original but not in current)
    originalArray.forEach(originalItem => {
      if (originalItem.alias && !processedOriginalAliases.has(originalItem.alias)) {
        // This item doesn't exist in current data - it's deleted
        changedItems.push({
          ...originalItem,
          type: 'delete'
        });
      }
    });

    return changedItems;
  };

  // Submit updated data
  const handleSubmit = async (formData) => {
    try {
      if (alias) {
        setIsSubmitting(true);

        // Get changed items for artists field
        const arrayFields = ['artists'];
        const changedData = {};
        const deletedData = {};
        let hasChanges = false;

        // Check for changes in array fields
        arrayFields.forEach(fieldName => {
          const changedItems = getChangedItems(formData, originalData, fieldName);

          // Separate added/updated items from deleted items
          const addsAndUpdates = changedItems.filter(item => item.type === 'add' || item.type === 'update');
          const deletes = changedItems.filter(item => item.type === 'delete');

          if (addsAndUpdates.length > 0) {
            changedData[fieldName] = addsAndUpdates;
            hasChanges = true;
          }

          if (deletes.length > 0) {
            // Format deleted items as { alias: 'value' } only
            deletedData[fieldName] = deletes.map(item => ({
              alias: item.alias
            }));
            hasChanges = true;
          }
        });

        if (hasChanges) {
          const updatedFormData = reverseTransformData(changedData, []);

          // Format deletedValues as required: { artists: [{ alias: 'value' }] }
          const deletedValues = {};
          if (deletedData.artists && deletedData.artists.length > 0) {
            deletedValues.artists = deletedData.artists;
          }

          const result = await updateEventMasterlistData(alias, updatedFormData, deletedValues);

          if (result?.success) {
            // Update originalData to reflect the new baseline after successful save
            const updatedOriginalData = { ...originalData };
            arrayFields.forEach(fieldName => {
              const currentArray = formData[fieldName] || [];
              updatedOriginalData[fieldName] = [...currentArray];
            });
            setOriginalData(updatedOriginalData);

            setQuickSave(prev => !prev);
            setProcessedData(formData); // Update processed data to reflect current form state
                        showSuccessToast(toast, "Data updated successfully");
          }
        } else {
          // No changes detected
                    showInfoToast(toast, "No changes detected");
        }
      }
    } catch (error) {
      console.error('Error updating data:', error);
            showErrorToast(toast, "Failed to update data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const widgets = {
    personalityAliasTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchPersonalityAliasSuggestions}
      />
    ),
  };

  return (
    <Box maxW="auto">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form schema={schema} uiSchema={uiSchema} formData={processedData} validator={validator} onSubmit={(e) => handleSubmit(e.formData)} widgets={widgets} />
      )}
    </Box>
  );
};

export default Highlights;